<template>
  <ag-grid-vue
    :id="`ascAgGrid${_uid}`"
    :ref="`ascAgGrid${_uid}`"
    class="ag-theme-balham"
    :style="computedStyles"
    :grid-options="gridOptions"
    :column-defs="columnDefs"
    :get-row-id="getRowId ? getRowId : undefined"
    :row-data="rowData"
    :group-default-expanded="groupDefaultExpanded"
    :master-detail="masterDetail"
    :detail-cell-renderer-params="detailCellRendererParams"
    :get-row-height="getRowHeight"
    row-height="auto"
    @row-selected="onRowSelected"
    @selection-changed="onSelectionChanged"
  ></ag-grid-vue>
</template>

<script>
import { AscAgGridPropsMixin } from '@/components/mixins'
import { AgGridVue } from '@ag-grid-community/vue'
import { ModuleRegistry, AllModules } from '@ag-grid-enterprise/all-modules'
import { LicenseManager } from '@ag-grid-enterprise/core'

LicenseManager.setLicenseKey(
  'CompanyName=SHI International Corp._on_behalf_of_UTC Collins Aerospace,LicensedApplication=Ascentia,LicenseType=SingleApplication,LicensedConcurrentDeveloperCount=5,LicensedProductionInstancesCount=1,AssetReference=AG-026010,ExpiryDate=1_March_2023_[v2]_MTY3NzYyODgwMDAwMA==6b8c9609c8aaf7962bbeef016b784d4a',
)

ModuleRegistry.registerModules(AllModules)

export default {
  components: {
    AgGridVue,
  },
  mixins: [AscAgGridPropsMixin],

  props: {
    getRowId: {
      type: Function,
      default: undefined,
    },
  },

  data() {
    return {}
  },

  computed: {
    isGridFiltersHidden() {
      return !this.gridOptions?.floatingFilter
    },

    computedStyles() {
      let heightVar = 'auto !important'

      if (typeof this.gridSize === 'string' && this.gridSize !== 'auto') {
        if (this.gridSize === 'max') {
          heightVar = 'calc(100vh - 250px)'
        } else if (this.gridSize === 'sm') {
          heightVar = '260px'
        } else if (this.isGridFiltersHidden) {
          heightVar = this.gridSize === 'lg' ? '525px' : '440px'
        } else {
          heightVar = this.gridSize === 'lg' ? '565px' : '480px'
        }
      } else if (typeof this.gridSize === 'number') {
        heightVar = `${this.gridSize}px`
      }

      return `width: 100%;height: ${heightVar}`
    },
  },

  methods: {
    rowId(row) {
      return row.data.id
    },
    onSelectionChanged(event) {
      this.$emit('onSelectionChanged', event.api.getSelectedNodes())
    },
    onRowSelected(event) {
      this.$emit('onRowSelected', event.node)
    },
  },
}
</script>

<style lang="scss">
@import '../../../node_modules/@ag-grid-enterprise/all-modules/dist/styles/ag-grid.scss';
@import '../../../node_modules/@ag-grid-enterprise/all-modules/dist/styles/ag-theme-balham.min.css';

.ag-theme-balham {
  .ag-root {
    border: none;
  }

  .ag-menu .ag-menu-separator {
    height: 2px;
    background-color: $primary-black;
  }

  .ag-paging-panel {
    color: $primary-black;
    font-size: 16px;

    & .ag-icon {
      color: $primary-black;
    }
  }

  .ag-header-container {
    background-color: $gray-600;
    color: $primary-white;
    font-size: 16px;
    font-weight: 500;
    text-align: left;
    border: none;
  }

  .ag-header-viewport {
    background-color: $gray-600;
  }

  .ag-header-cell::after,
  .ag-header-group-cell::after {
    border: none;
  }

  .ag-header {
    background-color: $primary-white;
    border: none;
    height: 48px;
  }

  .ag-header-cell {
    border: none;
    height: 48px;

    &-text {
      margin-right: 10px;
    }
  }

  .ag-header-row {
    border: none;
  }

  .ag-header-icon {
    cursor: pointer;

    .ag-icon {
      color: $primary-white;
      transform: none;
      opacity: 1;
    }

    &.ag-filter-icon {
      position: absolute;
      right: 40px;
      top: 18px;
    }

    .ag-icon-desc,
    .ag-icon-asc {
      position: absolute;
      right: 26px;
      top: 18px;
    }
  }

  .ag-row {
    border: none;
    height: 48px;
    border-bottom-color: $gray-300;

    &:hover {
      background-color: $gray-200;
    }
  }

  .ag-row-odd {
    background-color: $primary-white;
  }

  .ag-row-selected {
    background-color: $primary-white;
  }

  .ag-row-hover {
    background-color: $primary-white;
  }

  .highlightHoveredRows.ag-row-hover {
    background-color: $accent-0 !important;
    cursor: pointer;
  }

  .ag-cell {
    font-size: 14px;
    line-height: 48px;
    border-bottom-color: $gray-300;
    padding: 0 24px;

    .ag-icon.ag-icon-checkbox-unchecked {
      width: 17px;
      height: 17px;
      border: 1px solid $gray-600;
      border-radius: 0;

      &::before {
        font-family: 'Material Design Icons';
        content: '';
      }

      &::after {
        background-color: $accent-400;
      }
    }

    .ag-icon.ag-icon-checkbox-checked {
      width: 17px;
      height: 17px;
      background-color: $accent-400;
      border-radius: 0;

      &::before {
        font-family: 'Material Design Icons';
        content: '\F0E1E';
        color: $primary-black;
        position: absolute;
      }
    }

    &.ag-cell-focus {
      border: none !important;
      border-bottom: 1px solid $gray-300 !important;
    }

    .ag-header-cell {
      border: none;
      height: 48px;
    }

    .ag-header-row {
      border: none;
    }

    .ag-header-icon {
      cursor: pointer;

      .ag-icon {
        color: $primary-white;
        transform: none;
        opacity: 1;
      }

      &.ag-filter-icon {
        position: absolute;
        right: 40px;
        top: 15px;
      }

      .ag-icon-desc,
      .ag-icon-asc {
        position: absolute;
        right: 26px;
        top: 18px;
      }
    }

    .ag-row {
      border: none;
      height: 48px;

      &:hover {
        background-color: $gray-200;
      }
    }

    .ag-row-odd {
      background-color: $primary-white;
    }

    .ag-row-selected {
      background-color: $primary-white;
    }

    .ag-row-hover {
      background-color: $primary-white;
    }

    .ag-cell {
      font-size: 14px;
      line-height: 48px;
      border-bottom-color: $gray-300;
      padding: 0 24px;

      &.ag-cell-focus {
        border: none !important;
        border-bottom-color: $gray-300 !important;
        outline: none;
      }
    }
  }
}
</style>
